import React from "react";
import { useRillContext } from "../../../rill/RillReact/share/base";
import CreateTourModal from "./CreateTourModal";
import { meetingProviderLabel } from "./meeting-provider-label";
import SchedulingEmptyState from "./SchedulingEmptyState";
import { ToursListSkeleton } from "./SchedulingSkeletons";
import TourShareLinkPanel from "./TourShareLinkPanel";

const WEEKDAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function formatTime12(value) {
  if (typeof value !== "string") return "";
  const m = value.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return value;
  const h = Number(m[1]);
  const period = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${m[2]} ${period}`;
}

/** "Mon, Wed, Fri" from the consult's windows. */
function daysLabel(windows) {
  const days = [
    ...new Set(
      (windows ?? [])
        .map((w) => w.day_of_week ?? w.dayOfWeek)
        .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6)
    ),
  ].sort((a, b) => a - b);
  return days.length > 0 ? days.map((d) => WEEKDAY_SHORT[d]).join(", ") : "-";
}

/** Earliest start → latest end across all windows. */
function timeLabel(windows) {
  const list = windows ?? [];
  if (list.length === 0) return "-";
  const starts = list.map((w) => w.start_time ?? w.startTime).filter(Boolean);
  const ends = list.map((w) => w.end_time ?? w.endTime).filter(Boolean);
  if (starts.length === 0 || ends.length === 0) return "-";
  const min = starts.sort()[0];
  const max = ends.sort()[ends.length - 1];
  return `${formatTime12(min)} - ${formatTime12(max)}`;
}

function unassignedWindowCount(windows) {
  return (windows ?? []).filter((w) => !w.coachId).length;
}

/** Distinct coach names across the consult's windows, for the summary line. */
function coachSummary(windows, coachMap) {
  const names = [
    ...new Set(
      (windows ?? [])
        .map((w) => (w.coachId ? coachMap[String(w.coachId)]?.name : null))
        .filter(Boolean)
    ),
  ];
  if (names.length === 0) return "No coaches assigned";
  if (names.length <= 2) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
}

const SchedulingTours = () => {
  const { locals } = useRillContext();
  const theme = locals?.activeUser?.webAppTheme || "dark";
  const [tours, setTours] = React.useState([]);
  const [coachMap, setCoachMap] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(null);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editingTour, setEditingTour] = React.useState(null);
  const [expandedId, setExpandedId] = React.useState(null);

  const fetchTours = React.useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch("/actions/scheduling/tours", {
        credentials: "same-origin",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          data.error ?? data.message ?? "Failed to load consults"
        );
      }
      const data = await res.json();
      setTours(Array.isArray(data?.tours) ? data.tours : []);
      setCoachMap(data?.window_coaches ?? {});
    } catch (e) {
      setLoadError(e?.message ?? "Failed to load consults");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchTours();
  }, [fetchTours]);

  const openCreate = () => {
    setEditingTour(null);
    setModalOpen(true);
  };

  const openEdit = (tour) => {
    setEditingTour(tour);
    setModalOpen(true);
  };

  const deleteTour = async (tour) => {
    const id = String(tour?._id ?? tour?.id ?? "");
    if (!id) return;
    if (
      !window.confirm(
        "Archive this consult? The booking link stops working. Bookings already made are kept."
      )
    ) {
      return;
    }
    try {
      const res = await fetch(
        `/actions/scheduling/tours/${encodeURIComponent(id)}`,
        { method: "DELETE", credentials: "same-origin" }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          data.error ?? data.message ?? "Failed to archive consult"
        );
      }
      setExpandedId((prev) => (prev === id ? null : prev));
      fetchTours();
    } catch (e) {
      window.alert(e?.message ?? "Failed to archive consult");
    }
  };

  return (
    <div className="scheduling-classes scheduling-tours">
      <header className="scheduling-classes-header">
        <hgroup className="scheduling-classes-title-group">
          <h1 className="scheduling-classes-title">Consults</h1>
          <p className="scheduling-classes-subtitle">
            Let prospective members book a consult from a link or an embed on
            your website - no account required.
          </p>
        </hgroup>
        <div className="scheduling-classes-header-buttons">
          <button
            type="button"
            data-button-type="primary"
            className="scheduling-classes-create-btn"
            onClick={openCreate}
          >
            Create Consult
          </button>
        </div>
      </header>

      <div className="scheduling-classes-box">
        {loadError && (
          <p className="scheduling-classes-error" role="alert">
            {loadError}
          </p>
        )}

        {loading ? (
          <ToursListSkeleton theme={theme} />
        ) : tours.length === 0 ? (
          <SchedulingEmptyState
            title="No consults yet"
            description="Create a consult to get a shareable booking link for prospective members."
          />
        ) : (
          <ul className="scheduling-classes-list scheduling-tours-list">
            {tours.map((tour) => {
              const id = String(tour._id ?? tour.id);
              const windows = tour.windows ?? [];
              const unassigned = unassignedWindowCount(windows);
              const live = Boolean(
                tour.public_booking_enabled && tour.public_token
              );
              return (
                <li key={id} className="scheduling-pt-session-row-wrap">
                  <div className="scheduling-class-card scheduling-tour-card">
                    <button
                      type="button"
                      className="scheduling-pt-session-expand-btn scheduling-tour-card-expand"
                      aria-expanded={expandedId === id}
                      aria-label={
                        expandedId === id
                          ? "Hide booking link"
                          : "Show booking link"
                      }
                      onClick={() =>
                        setExpandedId(expandedId === id ? null : id)
                      }
                    >
                      <img
                        src={
                          expandedId === id
                            ? "/img/new-icons/vuesax-linear-arrow-square-down.svg"
                            : "/img/new-icons/vuesax-linear-arrow-square-right.svg"
                        }
                        alt=""
                      />
                    </button>

                    <div className="scheduling-tour-card-main">
                      <div className="scheduling-tour-card-header">
                        <div className="scheduling-tour-card-title-wrap">
                          <h3 className="scheduling-class-card-title">
                            {tour.name}
                          </h3>
                          <span
                            className={
                              live
                                ? "scheduling-pt-session-status scheduling-pt-session-status--active"
                                : "scheduling-pt-session-status scheduling-pt-session-status--paused"
                            }
                          >
                            {live ? "Booking live" : "Booking off"}
                          </span>
                        </div>

                        <div className="scheduling-class-card-actions">
                          <button
                            type="button"
                            className="scheduling-class-card-edit-btn"
                            onClick={() => openEdit(tour)}
                          >
                            <img src="/img/new-icons/edit-pencil.svg" alt="" />
                            Edit
                          </button>
                          <button
                            type="button"
                            className="scheduling-class-card-edit-btn"
                            onClick={() => deleteTour(tour)}
                          >
                            Archive
                          </button>
                        </div>
                      </div>

                      <div className="scheduling-tour-card-body">
                        <div className="scheduling-tour-card-meta">
                          <span className="scheduling-pt-session-meta-text">
                            {tour.duration_minutes} min ·{" "}
                            {tour.capacity == null
                              ? "unlimited guests"
                              : `${tour.capacity} guest${tour.capacity === 1 ? "" : "s"}/slot`}
                          </span>
                          {unassigned > 0 && (
                            <span className="scheduling-needs-coach-badge">
                              ⚠ {unassigned} unassigned{" "}
                              {unassigned === 1 ? "range" : "ranges"}
                            </span>
                          )}
                        </div>

                        <div className="scheduling-pt-session-columns">
                          <div className="scheduling-pt-session-col">
                            <span className="scheduling-pt-session-col-label">
                              Days
                            </span>
                            <span className="scheduling-pt-session-col-value">
                              {daysLabel(windows)}
                            </span>
                          </div>
                          <div className="scheduling-pt-session-col">
                            <span className="scheduling-pt-session-col-label">
                              Time
                            </span>
                            <span className="scheduling-pt-session-col-value">
                              {timeLabel(windows)}
                            </span>
                          </div>
                          <div className="scheduling-pt-session-col">
                            <span className="scheduling-pt-session-col-label">
                              Location
                            </span>
                            <span className="scheduling-pt-session-col-value">
                              {tour.location_type === "online"
                                ? `Online · ${meetingProviderLabel(tour.meeting_provider)}`
                                : tour.location?.trim() || "-"}
                            </span>
                          </div>
                          <div className="scheduling-pt-session-col">
                            <span className="scheduling-pt-session-col-label">
                              Coaches
                            </span>
                            <span className="scheduling-pt-session-col-value">
                              {coachSummary(windows, coachMap)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className={
                      expandedId === id
                        ? "scheduling-tour-expand is-open"
                        : "scheduling-tour-expand"
                    }
                    inert={expandedId === id ? undefined : ""}
                  >
                    <div className="scheduling-tour-expand-inner">
                      <div className="scheduling-tour-expanded">
                        <TourShareLinkPanel
                          tour={tour}
                          onChanged={fetchTours}
                        />
                        <div className="scheduling-tour-windows">
                          <h4 className="scheduling-tour-share-title">
                            Weekly availability
                          </h4>
                          <ul className="scheduling-tour-window-list">
                            {windows.map((w, i) => {
                              const firstOfDay =
                                i === 0 ||
                                windows[i - 1].day_of_week !== w.day_of_week;
                              return (
                                <li
                                  key={String(
                                    w._id ??
                                      `${w.day_of_week}-${w.start_time}-${w.end_time}`
                                  )}
                                  className="scheduling-tour-window-row"
                                >
                                  <span className="scheduling-tour-window-day">
                                    {firstOfDay
                                      ? (WEEKDAY_SHORT[w.day_of_week] ?? "-")
                                      : ""}
                                  </span>
                                  <span className="scheduling-tour-window-time">
                                    {formatTime12(w.start_time)} -{" "}
                                    {formatTime12(w.end_time)}
                                  </span>
                                  {w.coachId ? (
                                    <span className="scheduling-tour-window-coach">
                                      {coachMap[String(w.coachId)]?.name ??
                                        "Coach"}
                                    </span>
                                  ) : (
                                    <span className="scheduling-needs-coach-badge">
                                      ⚠ Unassigned
                                    </span>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <CreateTourModal
        isOpen={modalOpen}
        editTour={editingTour}
        onClose={() => setModalOpen(false)}
        onSaved={fetchTours}
      />
    </div>
  );
};

export default SchedulingTours;
